import type { TakiConfig } from '@taki/runtime-config';

/**
 * LA CONNEXION LOCALE SANS SAISIE : la decision, isolee et pure.
 *
 * LE PROBLEME QU'ELLE REGLE. La doctrine du depot demande d'OUVRIR L'ECRAN
 * avant de le livrer (AGENTS.md, section 12). Un agent, lui, ne tape jamais un
 * mot de passe, meme bidon, meme local : il s'arrete a la page de connexion et
 * livre un ecran qu'il n'a pas vu. Le vert du build ne dit rien du rendu, donc
 * l'ecran part non verifie. Il faut que l'entree en dev local ne demande RIEN.
 *
 * CE QUE CE MODULE EST, ET N'EST PAS. Deux fonctions pures : ni React, ni
 * Firebase, ni horloge. Elles ne CONNECTENT personne, elles repondent a deux
 * questions ("quel compte, s'il y en a un ?" et "faut-il entrer maintenant ?").
 * C'est ce qui les rend testables sans navigateur et sans emulateur, alors que
 * le mecanisme complet ne s'observe qu'a l'ecran. L'effet de bord vit dans
 * `AuthGuard.tsx`, le geste manuel dans `LoginScreen.tsx`.
 *
 * LE DOUBLE VERROU, parce qu'un seul se contourne par accident :
 *  1. la CONFIG : le compte ne vit que dans `emulators.connexion`, et le bloc
 *     `emulators` est par contrat absent en production (voir son commentaire
 *     dans `@taki/runtime-config`). Le porter obligerait a porter aussi le
 *     branchement sur des emulateurs a 127.0.0.1.
 *  2. le CODE : `surEmulateur`, qui vient de `isAuthOnEmulator()` et n'est vrai
 *     qu'apres un vrai `connectAuthEmulator`.
 * Aucun des deux ne suffit seul. C'est le sens de la premiere ligne de
 * `connexionLocaleDe`.
 */

/** Le compte de test retenu, `auto` deja tranche (plus d'absence a interpreter). */
export interface ConnexionLocale {
  email: string;
  password: string;
  /** L'app doit-elle entrer seule au demarrage ? */
  auto: boolean;
}

/**
 * Le compte local utilisable, ou `null` s'il n'y en a pas.
 *
 * @param config - La config chargee au runtime.
 * @param surEmulateur - Auth parle-t-il vraiment a un emulateur (`isAuthOnEmulator()`) ?
 *   C'est le second verrou : a `false`, la reponse est `null` quoi que dise la config.
 */
export function connexionLocaleDe(
  config: TakiConfig,
  surEmulateur: boolean,
): ConnexionLocale | null {
  if (!surEmulateur) {
    return null;
  }
  const connexion = config.emulators?.connexion;
  if (connexion === undefined) {
    return null;
  }
  // `auto` ABSENT vaut VRAI : entrer seul est le but du mecanisme, et un poste
  // dont la config a ete ecrite avant l'arrivee de la cle doit en profiter sans
  // rien reecrire. Seul un `false` explicite garde le formulaire.
  return {
    email: connexion.email,
    password: connexion.password,
    auto: connexion.auto !== false,
  };
}

/* -------------------------------------------------------------------------- */
/* LE VERROU DE LA PAGE                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A-t-on deja eu quelqu'un de connecte sur CETTE page ?
 *
 * IL VIT AU NIVEAU DU MODULE, ET C'EST TOUT LE SUJET. Il a d'abord ete une
 * `ref` dans l'auth guard, ce qui paraissait suffisant : une ref survit aux
 * rendus. Elle ne survit pas au DEMONTAGE, et le guard se remonte pour de vrai
 * (l'hote intercale « Verification de tes droits en cours... » pendant qu'il
 * lit la fiche membre, ce qui retire le shell de l'arbre). Le verrou repartait
 * donc a zero au pire moment : juste apres un refus d'entree, celui ou l'on
 * clique « Essayer une autre adresse ». On ressortait sur le compte de demo.
 *
 * Le fait est celui d'un CHARGEMENT DE PAGE, pas celui d'un composant : il vit
 * donc la ou un chargement de page vit, dans le module.
 */
let entreeJouee = false;

/** Le verrou est-il ferme ? */
export function entreeDejaJouee(): boolean {
  return entreeJouee;
}

/**
 * Ferme le verrou, pour de bon.
 *
 * Deux moments l'appellent, et les deux comptent :
 *   - une entree automatique VIENT d'etre lancee (sans ca, elle boucle) ;
 *   - un utilisateur est SIMPLEMENT LA, session restauree du disque comprise.
 *     Ce second cas est celui que le premier ratait : personne n'avait rien
 *     « tente », donc le verrou restait ouvert, et le premier
 *     « Se deconnecter » declenchait une entree automatique par-dessus.
 */
export function marquerEntreeJouee(): void {
  entreeJouee = true;
}

/** Rouvre le verrou. Reserve aux TESTS : une page ne se recharge pas a moitie. */
export function reinitialiserEntreeJouee(): void {
  entreeJouee = false;
}

/** L'etat vu par {@link doitEntrerSeul}, reduit a ce qui decide. */
export interface EtatEntreeAutomatique {
  /** Le compte local, tel que rendu par {@link connexionLocaleDe}. */
  compte: ConnexionLocale | null;
  /** L'etat d'auth initial est-il encore en cours de resolution ? */
  loading: boolean;
  /** Un utilisateur est-il deja connecte ? */
  connecte: boolean;
  /** A-t-on deja tente l'entree automatique depuis le chargement de la page ? */
  dejaTente: boolean;
}

/**
 * Faut-il entrer seul, maintenant ?
 *
 * Les cinq conditions sont toutes necessaires, et deux d'entre elles sont la
 * pour des degats precis :
 *  - `loading` : entrer avant que Firebase ait dit qui est connecte lancerait
 *    une connexion pour rien, par-dessus une session deja valide.
 *  - `dejaTente` : ON N'ENTRE SEUL QUE DANS UNE PAGE OU PERSONNE N'EST ENTRE.
 *    Sans ca, se deconnecter volontairement rendrait la main au guard, qui
 *    reconnecterait aussitot : le bouton « Se deconnecter » ne servirait plus a
 *    rien, et un echec d'identifiants bouclerait a l'infini. Le verrou lui-meme
 *    vit dans ce module (`entreeDejaJouee`), pas dans le guard : voir son
 *    commentaire, le demontage du guard l'avait deja rouvert une fois.
 */
export function doitEntrerSeul(etat: EtatEntreeAutomatique): boolean {
  if (etat.compte === null) {
    return false;
  }
  if (!etat.compte.auto) {
    return false;
  }
  return !etat.loading && !etat.connecte && !etat.dejaTente;
}
